import React from 'react';
import { Link } from 'react-router-dom';
import { MapPinIcon, BuildingOfficeIcon, TruckIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';

const Location: React.FC = () => {
  return (
    <div className="bg-white">
      <div className="max-w-7xl mx-auto py-24 px-4 sm:px-6 lg:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 pt-8"
        >
          <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl">
            Conference Location
          </h1>
          <p className="mt-4 text-xl text-gray-600 max-w-3xl mx-auto">
            Join us at the prestigious Austrian Parliament in the heart of Vienna
          </p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid md:grid-cols-2 gap-12 items-center mb-16"
        >
          <div>
            <img 
              src="/parliment-1.jpg" 
              alt="Austrian Parliament" 
              className="rounded-xl shadow-lg w-full h-auto object-cover"
            />
          </div>
          <div className="space-y-6">
            <div className="flex items-start">
              <MapPinIcon className="h-8 w-8 text-[#00529B] mr-4 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-[#00529B] mb-2">Address</h2>
                <p className="text-gray-700">
                  Dr. Karl Renner-Ring 3<br />
                  A-1017 Wien<br />
                  Austria
                </p>
                <div className="mt-4">
                  <a 
                    href="https://maps.app.goo.gl/Q59RTMaQaXtvr31k7" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-[#00529B] text-white rounded-md hover:bg-[#003D74] transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    View on Google Maps
                  </a>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-gray-50 rounded-xl p-8 mb-16"
        >
          <div className="flex items-start mb-6">
            <TruckIcon className="h-8 w-8 text-[#00529B] mr-4 flex-shrink-0" />
            <div>
              <h2 className="text-2xl font-bold text-[#00529B] mb-4">How to get there?</h2>
              <div className="prose prose-lg max-w-none text-gray-700">
                <p>
                  <strong>By train from the airport:</strong> the Vienna Airport (Flughafen Wien) is about 30 minutes away from the city center (by car). Connecting trains leave regularly from the Airport to the Vienna Central Station (Wien Hauptbahnhof). The train ride takes abut 20 minutes.
                </p>
                <p>
                  From the Hauptbahnhof, take a taxi or tram line D to the city centre, stop Volkstheater/Ring or Parliament. This takes about 20 minutes.
                </p>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-bold text-[#00529B] mb-3">Public Transportation</h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-[#00529B] mt-1 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span><strong>Subway:</strong> U2, U3 - 5 min walk</span>
                  </li>
                  <li className="flex items-start">
                    <svg className="w-5 h-5 text-[#00529B] mt-1 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span><strong>Tram:</strong> Lines 1, 2, 71, D - 1 min walk</span>
                  </li>

                </ul>
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#00529B] mb-3">By Car</h3>
                <p className="text-gray-700 mb-4">
                  There are two underground park garages next to the Parliament: <a href="https://www.boe-parking.at/de/garagen/wien/rathauspark" target="_blank" rel="noopener noreferrer"><strong>Rathauspark</strong></a> & <a href="https://www.boe-parking.at/de/garagen/wien/schmerlingplatz" target="_blank" rel="noopener noreferrer"><strong>Schmerlingplatz</strong></a>.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="bg-white rounded-xl p-8 shadow-lg"
        >
          <div className="flex items-start mb-6">
            <BuildingOfficeIcon className="h-8 w-8 text-[#00529B] mr-4 flex-shrink-0" />
            <div>
              <h2 className="text-2xl font-bold text-[#00529B] mb-4">Hotels</h2>
              <p className="text-gray-700 mb-6">
                Here is a list of hotels in the vicinity of our venue:
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { name: "The Levante", distance: "1.6 km", url: "https://www.thelevante.com" },
              { name: "Flemings Hotel Vienna", distance: "0.4 km", url: "https://www.flemings-hotels.com/en/wien-city-selection-hotel" },
              { name: "Hotel-Pension Columbia", distance: "1.1 km", url: "https://www.hotel-columbia.at/en/hotel-columbia/" },
              { name: "25hours Hotel Vienna", distance: "0.3 km", url: "https://25hours-hotels.com/de/vienna/at-museumsquartier/" },
              { name: "Artist Hotel Vienna", distance: "1.1 km", url: "https://www.artist-hotel.at" },
              { name: "Rathauspark Hotel", distance: "0.7 km", url: "https://www.radissonhotels.com/en-us/hotels/radisson-individuals-rathauspark-wien" }
            ].map((hotel, index) => (
              <motion.div 
                key={hotel.name}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.7 + (index * 0.1) }}
                className="bg-gray-50 p-6 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <h3 className="text-lg font-bold text-[#00529B] mb-2">{hotel.name}</h3>
                <p className="text-gray-600 text-sm mb-3">{hotel.distance} from venue</p>
                <div className="flex items-center justify-between">
                  <a 
                    href={hotel.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-[#00529B] hover:text-[#003D74] text-sm font-medium"
                  >
                    Visit website
                  </a>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Location;
